import { jsx as _jsx } from "react/jsx-runtime";
/* eslint-disable max-len */
/* eslint-disable dot-notation */
/* eslint-disable no-unused-vars */
import React, { useRef, useEffect, useCallback } from 'react';
import { TitleInfo, useReady, useClosestVitessceContainerSize, useWindowDimensions, useCoordination, useLoaders, useAuxiliaryCoordination, useComponentLayout, useMultiObsSpots, useMultiObsPoints, useMultiObsSegmentations, useMultiImages, usePointMultiObsFeatureMatrixIndices, useMergeCoordination, useComplexCoordination, useMultiCoordinationScopesNonNull, useMultiCoordinationScopesSecondaryNonNull, useComplexCoordinationSecondary, useCoordinationScopes, useCoordinationScopesBy, } from '@vitessce/vit-s';
import { ViewType, CoordinationType, COMPONENT_COORDINATION_TYPES, ViewHelpMapping, } from '@vitessce/constants-internal';
import LayerController from './LayerController.js';
/**
 * A subscriber component for the spatial layer controller.
 * @param {object} props
 * @param {string} props.theme The current theme name.
 * @param {object} props.coordinationScopes The mapping from coordination types to coordination
 * scopes.
 * @param {function} props.removeGridComponent The callback function to pass to TitleInfo,
 * to call when the component has been removed from the grid.
 * @param {string} props.title The component title.
 * @param {boolean} props.globalDisable3d Hide the 3D rendering-mode switch, so this view cannot
 * enter volume rendering. Named to match the equivalent prop on the legacy `layerController`.
 * @param {object[]} props.cameraPresets An array of camera preset objects,
 * which can be applied by the user by pressing CTRL+[0-9].
 * Each preset object can have the following coordination properties:
 * spatialTargetX, spatialTargetY, spatialTargetZ, spatialTargetT,
 * spatialRotationX, spatialRotationOrbit, and spatialZoom.
 */
export function LayerControllerSubscriber(props) {
    const { coordinationScopes: coordinationScopesRaw, coordinationScopesBy: coordinationScopesByRaw, closeButtonVisible, downloadButtonVisible, removeGridComponent, theme, title = 'Spatial Layers', uuid, layerPerFeatureForPoints = false, globalDisable3d = false, cameraPresets, helpText = ViewHelpMapping.LAYER_CONTROLLER_BETA, } = props;
    const loaders = useLoaders();
    const mergeCoordination = useMergeCoordination();
    const coordinationScopes = useCoordinationScopes(coordinationScopesRaw);
    const coordinationScopesBy = useCoordinationScopesBy(coordinationScopes, coordinationScopesByRaw);
    // Get "props" from the coordination space.
    const [{ dataset, spatialTargetT: targetT, spatialTargetZ: targetZ, spatialRenderingMode, }, { setSpatialTargetX: setTargetX, setSpatialTargetY: setTargetY, setSpatialTargetZ: setTargetZ, setSpatialTargetT: setTargetT, setSpatialRotationX: setRotationX, setSpatialRotationOrbit: setRotationOrbit, setSpatialZoom: setZoom, setSpatialRenderingMode, },] = useCoordination(COMPONENT_COORDINATION_TYPES[ViewType.LAYER_CONTROLLER_BETA], coordinationScopes);
    // Apply a camera preset by calling the coordination setters.
    const applyPreset = useCallback((preset) => {
        if (!preset)
            return;
        if (typeof preset?.spatialZoom === 'number')
            setZoom(preset.spatialZoom);
        if (typeof preset?.spatialTargetX === 'number')
            setTargetX(preset.spatialTargetX);
        if (typeof preset?.spatialTargetY === 'number')
            setTargetY(preset.spatialTargetY);
        if (typeof preset?.spatialTargetZ === 'number')
            setTargetZ(preset.spatialTargetZ);
        if (typeof preset?.spatialTargetT === 'number')
            setTargetT(preset.spatialTargetT);
        if (typeof preset?.spatialRotationX === 'number')
            setRotationX(preset.spatialRotationX);
        if (typeof preset?.spatialRotationOrbit === 'number')
            setRotationOrbit(preset.spatialRotationOrbit);
    }, [setZoom, setTargetX, setTargetY, setTargetZ, setTargetT, setRotationX, setRotationOrbit]);
    // Listen for CTRL + digit keypresses to apply camera presets
    useEffect(() => {
        // Do not set up any listeners if no camera presets were passed.
        if (!Array.isArray(cameraPresets))
            return undefined;
        const handleKeyDown = (e) => {
            const isCtrl = e.ctrlKey;
            if (!isCtrl)
                return;
            const digitMatch = e.code.match(/^Digit(\d)$/);
            if (!digitMatch || !Array.isArray(digitMatch) || digitMatch.length < 2)
                return;
            // If we get to this point, digitMatch is an array like ['Digit1', '1'],
            // so the digit is at index 1 as a string.
            const index = parseInt(digitMatch[1], 10);
            if (index >= 0 && index <= 9 && index < cameraPresets.length) {
                const preset = cameraPresets[index];
                applyPreset(preset);
                e.preventDefault();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [cameraPresets, applyPreset]);
    // Normalize arrays and non-arrays to always be arrays.
    const [segmentationLayerScopes, segmentationChannelScopesByLayer] = useMultiCoordinationScopesSecondaryNonNull(CoordinationType.SEGMENTATION_CHANNEL, CoordinationType.SEGMENTATION_LAYER, coordinationScopes, coordinationScopesBy);
    const [imageLayerScopes, imageChannelScopesByLayer] = useMultiCoordinationScopesSecondaryNonNull(CoordinationType.IMAGE_CHANNEL, CoordinationType.IMAGE_LAYER, coordinationScopes, coordinationScopesBy);
    const spotLayerScopes = useMultiCoordinationScopesNonNull(CoordinationType.SPOT_LAYER, coordinationScopes);
    const pointLayerScopes = useMultiCoordinationScopesNonNull(CoordinationType.POINT_LAYER, coordinationScopes);
    // Object keys are coordination scope names for spatialSegmentationLayer.
    const segmentationLayerCoordination = useComplexCoordination([
        CoordinationType.FILE_UID,
        CoordinationType.SEGMENTATION_CHANNEL,
        CoordinationType.SPATIAL_LAYER_VISIBLE,
        CoordinationType.SPATIAL_LAYER_OPACITY,
    ], coordinationScopes, coordinationScopesBy, CoordinationType.SEGMENTATION_LAYER);
    // Object keys are coordination scope names for spatialSegmentationChannel.
    const segmentationChannelCoordination = useComplexCoordinationSecondary([
        CoordinationType.OBS_TYPE,
        CoordinationType.SPATIAL_TARGET_C,
        CoordinationType.SPATIAL_CHANNEL_VISIBLE,
        CoordinationType.SPATIAL_CHANNEL_OPACITY,
        CoordinationType.SPATIAL_CHANNEL_COLOR,
        CoordinationType.SPATIAL_SEGMENTATION_FILLED,
        CoordinationType.SPATIAL_SEGMENTATION_STROKE_WIDTH,
        CoordinationType.OBS_COLOR_ENCODING,
        CoordinationType.FEATURE_SELECTION,
        CoordinationType.FEATURE_VALUE_COLORMAP,
        CoordinationType.FEATURE_VALUE_COLORMAP_RANGE,
        CoordinationType.TOOLTIPS_VISIBLE,
        CoordinationType.TOOLTIP_CROSSHAIRS_VISIBLE,
        CoordinationType.LEGEND_VISIBLE,
    ], coordinationScopes, coordinationScopesBy, CoordinationType.SEGMENTATION_LAYER, CoordinationType.SEGMENTATION_CHANNEL);
    const imageLayerCoordination = useComplexCoordination([
        CoordinationType.FILE_UID,
        CoordinationType.IMAGE_CHANNEL,
        CoordinationType.SPATIAL_LAYER_VISIBLE,
        CoordinationType.SPATIAL_LAYER_OPACITY,
        CoordinationType.SPATIAL_LAYER_COLORMAP,
        CoordinationType.SPATIAL_LAYER_TRANSPARENT_COLOR,
        CoordinationType.SPATIAL_LAYER_MODEL_MATRIX,
        CoordinationType.PHOTOMETRIC_INTERPRETATION,
        CoordinationType.VOLUMETRIC_RENDERING_ALGORITHM,
        CoordinationType.SPATIAL_TARGET_RESOLUTION,
        CoordinationType.SPATIAL_LOD_FACTOR,
        CoordinationType.SPATIAL_SLICE_X,
        CoordinationType.SPATIAL_SLICE_Y,
        CoordinationType.SPATIAL_SLICE_Z,
        CoordinationType.TOOLTIPS_VISIBLE,
        CoordinationType.SPATIAL_CHANNEL_LABELS_VISIBLE,
        CoordinationType.SPATIAL_CHANNEL_LABELS_ORIENTATION,
        CoordinationType.SPATIAL_CHANNEL_LABEL_SIZE,
        CoordinationType.SPATIAL_CHANNELS_SORT_ORDER,
    ], coordinationScopes, coordinationScopesBy, CoordinationType.IMAGE_LAYER);
    // Object keys are coordination scope names for spatialImageChannel.
    const imageChannelCoordination = useComplexCoordinationSecondary([
        CoordinationType.SPATIAL_TARGET_C,
        CoordinationType.SPATIAL_CHANNEL_VISIBLE,
        CoordinationType.SPATIAL_CHANNEL_COLOR,
        CoordinationType.SPATIAL_CHANNEL_WINDOW,
        CoordinationType.SPATIAL_MAX_RESOLUTION,
    ], coordinationScopes, coordinationScopesBy, CoordinationType.IMAGE_LAYER, CoordinationType.IMAGE_CHANNEL);
    // Spot layer
    const spotLayerCoordination = useComplexCoordination([
        CoordinationType.OBS_TYPE,
        CoordinationType.SPATIAL_LAYER_VISIBLE,
        CoordinationType.SPATIAL_LAYER_OPACITY,
        CoordinationType.SPATIAL_SPOT_RADIUS,
        CoordinationType.SPATIAL_SPOT_FILLED,
        CoordinationType.SPATIAL_SPOT_STROKE_WIDTH,
        CoordinationType.OBS_COLOR_ENCODING,
        CoordinationType.FEATURE_SELECTION,
        CoordinationType.FEATURE_VALUE_COLORMAP,
        CoordinationType.FEATURE_VALUE_COLORMAP_RANGE,
        CoordinationType.SPATIAL_LAYER_COLOR,
        CoordinationType.TOOLTIPS_VISIBLE,
        CoordinationType.TOOLTIP_CROSSHAIRS_VISIBLE,
        CoordinationType.LEGEND_VISIBLE,
    ], coordinationScopes, coordinationScopesBy, CoordinationType.SPOT_LAYER);
    // Point layer
    const pointLayerCoordination = useComplexCoordination([
        CoordinationType.OBS_TYPE,
        CoordinationType.SPATIAL_LAYER_VISIBLE,
        CoordinationType.SPATIAL_LAYER_OPACITY,
        CoordinationType.SPATIAL_SPOT_RADIUS,
        CoordinationType.OBS_COLOR_ENCODING,
        CoordinationType.FEATURE_COLOR,
        CoordinationType.FEATURE_FILTER_MODE,
        CoordinationType.FEATURE_SELECTION,
        CoordinationType.FEATURE_VALUE_COLORMAP,
        CoordinationType.FEATURE_VALUE_COLORMAP_RANGE,
        CoordinationType.SPATIAL_LAYER_COLOR,
        CoordinationType.TOOLTIPS_VISIBLE,
        CoordinationType.TOOLTIP_CROSSHAIRS_VISIBLE,
        CoordinationType.LEGEND_VISIBLE,
    ], coordinationScopes, coordinationScopesBy, CoordinationType.POINT_LAYER);
    // Pair point layers (centroids) with their matching segmentation layers
    // by matching obsType between the two coordination objects.
    const centroidSegmentationPairs = React.useMemo(() => {
        const pairs = [];
        const pairedSegScopes = new Set();
        const pairedPointScopes = new Set();
        pointLayerScopes.forEach((pointScope) => {
            const pointCoord = pointLayerCoordination[0][pointScope];
            if (!pointCoord)
                return;
            const pointObsType = pointCoord[CoordinationType.OBS_TYPE];
            if (!pointObsType)
                return;
            const matchingSegScope = segmentationLayerScopes.find((segScope) => {
                if (pairedSegScopes.has(segScope))
                    return false;
                const channelScopes = segmentationChannelScopesByLayer[segScope] || [];
                return channelScopes.some((chanScope) => {
                    const chanCoord = segmentationChannelCoordination[0]?.[segScope]?.[chanScope];
                    return chanCoord?.[CoordinationType.OBS_TYPE] === pointObsType;
                });
            });
            if (matchingSegScope) {
                pairs.push({ pointScope, segScope: matchingSegScope });
                pairedSegScopes.add(matchingSegScope);
                pairedPointScopes.add(pointScope);
            }
        });
        return { pairs, pairedSegScopes, pairedPointScopes };
    }, [
        pointLayerScopes,
        pointLayerCoordination,
        segmentationLayerScopes,
        segmentationChannelScopesByLayer,
        segmentationChannelCoordination,
    ]);
    // Get volume loading status from auxiliary coordination (shared with Spatial view)
    const [{ volumeLoadingProgress: volumeLoadingStatus, tiledPointsLoadingProgress, },] = useAuxiliaryCoordination([
        'spatialAcceleratedVolumeLoadingProgress',
        'spatialTiledPointsLoadingProgress',
    ], coordinationScopes);
    /*
    const [
      {
        imageLayerCallbacks,
        areLoadingImageChannels,
        segmentationLayerCallbacks,
        areLoadingSegmentationChannels,
      },
      {
        setImageLayerCallbacks,
        setAreLoadingImageChannels,
        setSegmentationLayerCallbacks,
        setAreLoadingSegmentationChannels,
      },
    ] = useAuxiliaryCoordination(
      COMPONENT_COORDINATION_TYPES.layerController,
      coordinationScopes,
    );
    */
    // Spatial layout + window size is needed for the "re-center" button to work properly.
    // Dimensions of the Spatial component can be inferred and used for resetting view state to
    // a nice, centered view.
    const [spatialLayout] = useComponentLayout('spatial', ['spatialImageLayer'], coordinationScopes);
    const layerControllerRef = useRef();
    const [componentWidth, componentHeight] = useClosestVitessceContainerSize(layerControllerRef);
    const { height: windowHeight, width: windowWidth } = useWindowDimensions();
    const [obsSegmentationsData, obsSegmentationsDataStatus, obsSegmentationsUrls, obsSegmentationsErrors] = useMultiObsSegmentations(coordinationScopes, coordinationScopesBy, loaders, dataset, mergeCoordination, uuid);
    const [imageData, imageDataStatus, imageUrls, imageErrors] = useMultiImages(coordinationScopes, coordinationScopesBy, loaders, dataset, mergeCoordination, uuid);
    const [obsSpotsData, obsSpotsDataStatus, obsSpotsUrls, obsSpotsErrors] = useMultiObsSpots(coordinationScopes, coordinationScopesBy, loaders, dataset, mergeCoordination, uuid);
    const [obsPointsData, obsPointsDataStatus, obsPointsUrls, obsPointsErrors] = useMultiObsPoints(coordinationScopes, coordinationScopesBy, loaders, dataset, mergeCoordination, uuid);
    const [pointMultiIndicesData, pointMultiIndicesDataStatus, pointMultiIndicesDataErrors] = usePointMultiObsFeatureMatrixIndices(coordinationScopes, coordinationScopesBy, loaders, dataset);
    // Consolidate error values from data hooks.
    const errors = [
        ...obsSegmentationsErrors,
        ...imageErrors,
        ...obsSpotsErrors,
        ...obsPointsErrors,
        ...pointMultiIndicesDataErrors,
    ];
    const isReady = useReady([
        obsSpotsDataStatus,
        obsPointsDataStatus,
        obsSegmentationsDataStatus,
        imageDataStatus,
        pointMultiIndicesDataStatus,
    ]);
    return (_jsx(TitleInfo, { title: title, isScroll: true, closeButtonVisible: closeButtonVisible, downloadButtonVisible: downloadButtonVisible, removeGridComponent: removeGridComponent, theme: theme, isReady: isReady, errors: errors, helpText: helpText, children: _jsx(LayerController, { theme: theme, coordinationScopesRaw: coordinationScopesRaw, segmentationLayerScopes: segmentationLayerScopes, segmentationLayerCoordination: segmentationLayerCoordination, segmentationChannelScopesByLayer: segmentationChannelScopesByLayer, segmentationChannelCoordination: segmentationChannelCoordination, images: imageData, imageLayerScopes: imageLayerScopes, imageLayerCoordination: imageLayerCoordination, targetT: targetT, targetZ: targetZ, setTargetT: setTargetT, setTargetZ: setTargetZ, spatialRenderingMode: spatialRenderingMode, setSpatialRenderingMode: setSpatialRenderingMode, imageChannelScopesByLayer: imageChannelScopesByLayer, imageChannelCoordination: imageChannelCoordination, spotLayerScopes: spotLayerScopes, spotLayerCoordination: spotLayerCoordination, pointLayerScopes: pointLayerScopes, pointLayerCoordination: pointLayerCoordination, pointMultiIndicesData: pointMultiIndicesData, layerPerFeatureForPoints: layerPerFeatureForPoints, globalDisable3d: globalDisable3d, volumeLoadingStatus: volumeLoadingStatus, tiledPointsLoadingProgress: tiledPointsLoadingProgress, centroidSegmentationPairs: centroidSegmentationPairs.pairs, pairedSegScopes: centroidSegmentationPairs.pairedSegScopes, pairedPointScopes: centroidSegmentationPairs.pairedPointScopes }) }));
}
//# sourceMappingURL=LayerControllerSubscriber.js.map